import { lazy } from 'react'
import { RouteProps } from 'react-router'

export interface PrivateRouteObject extends RouteProps {
  exact: boolean
  path: string
  breadcrumb: string
  component: any
  title: string
}

const Home = lazy(() => import('../containers/Home'))
const Profile = lazy(() => import('../containers/Profile'))
const Login = lazy(() => import('../containers/Login'))
const Register = lazy(() => import('../containers/Register'))
const ConfirmEmail = lazy(() => import('../containers/ConfirmEmail'))
const ForgotPassword = lazy(() => import('../containers/ForgotPassword'))
const ResetPassword = lazy(() => import('../containers/ResetPassword'))
const LoginRedirect = lazy(() => import('../containers/SSOLoginRedirect'))
const CityResults = lazy(() => import('../containers/CityResults'))
const ProvinceResults = lazy(() => import('../containers/ProvinceResults'))
const CategoryResults = lazy(() => import('../containers/CategoryResults'))
const SuburbResults = lazy(() => import('../containers/SuburbResults'))
const DistrictResults = lazy(() => import('../containers/DistrictResults'))
const AdvancedSearch = lazy(() => import('../containers/AdvancedSearch'))
const PropertyProfile = lazy(() => import('../containers/PropertyProfile'))
const AboutUs = lazy(() => import('../containers/AboutUs'))
const Search = lazy(() => import('../containers/Search'))
const Team = lazy(() => import('../containers/Team'))
const Story = lazy(() => import('../containers/Story'))
const HowItWorks = lazy(() => import('../containers/HowItWorks'))
const UseCases = lazy(() => import('../containers/UseCases'))
const Privacy = lazy(() => import('../containers/Privacy'))
const Terms = lazy(() => import('../containers/Terms'))
const Careers = lazy(() => import('../containers/Careers'))
const AdminDashboard = lazy(() => import('../containers/AdminDashboard'))
const AdminTeamManagement = lazy(() => import('../containers/AdminTeamManagement'))
const Settings = lazy(() => import('../containers/Settings'))

const PRIVATE_ROUTES: PrivateRouteObject[] = [
  {
    exact: false,
    path: '/auth/dashboard/agent/dashboard',
    breadcrumb: 'Admin Dashboard',
    component: AdminDashboard,
    title: 'Admin Dashboard'
  },
  {
    exact: false,
    path: '/auth/dashboard/agent/team-management',
    breadcrumb: 'Team Management',
    component: AdminTeamManagement,
    title: 'Team Management'
  },
  {
    exact: false,
    path: '/auth/profile',
    breadcrumb: 'My Profile',
    component: Profile,
    title: 'My Profile'
  },
  {
    exact: false,
    path: '/auth/dashboard/settings/agency-info',
    breadcrumb: 'My Settings',
    component: Settings,
    title: 'My Settings'
  },
  {
    exact: true,
    path: '/auth',
    breadcrumb: 'Home',
    component: Home,
    title: 'Home'
  },
  {
    exact: true,
    path: '/auth/search',
    breadcrumb: 'Search',
    component: Search,
    title: 'Search'
  },
  {
    exact: true,
    path: '/auth/about-us',
    breadcrumb: 'about-us',
    component: AboutUs,
    title: 'About us'
  },
  {
    exact: true,
    path: '/auth/history',
    breadcrumb: 'story',
    component: Story,
    title: 'Story'
  },
  {
    exact: true,
    path: '/auth/how-it-works',
    breadcrumb: 'how it works',
    component: HowItWorks,
    title: 'How It Works'
  },
  {
    exact: true,
    path: '/auth/use-cases',
    breadcrumb: 'use cases',
    component: UseCases,
    title: 'Use Cases'
  },
  {
    exact: true,
    path: '/auth/privacy',
    breadcrumb: 'privacy',
    component: Privacy,
    title: 'Privacy'
  },
  {
    exact: true,
    path: '/auth/terms',
    breadcrumb: 'terms',
    component: Terms,
    title: 'Terms'
  },
  {
    exact: true,
    path: '/auth/team',
    breadcrumb: 'team',
    component: Team,
    title: 'Team'
  },
  {
    exact: true,
    path: '/auth/career',
    breadcrumb: 'career',
    component: Careers,
    title: 'Career'
  },
  {
    exact: true,
    path: '/auth/simple-search/:category/:province',
    breadcrumb: 'results',
    component: ProvinceResults,
    title: 'Properties'
  },
  {
    exact: true,
    path: '/auth/property/:id',
    breadcrumb: 'property',
    component: PropertyProfile,
    title: 'Property'
  },
  {
    exact: true,
    path: '/auth/simple-search/:category/:province/:district/:city',
    breadcrumb: 'results',
    component: CityResults,
    title: 'Properties'
  },
  {
    exact: true,
    path: '/auth/simple-search/:category/:province/:district',
    breadcrumb: 'results',
    component: DistrictResults,
    title: 'Properties'
  },
  {
    exact: true,
    path: '/auth/simple-search/:category',
    breadcrumb: 'results',
    component: CategoryResults,
    title: 'Properties'
  },
  {
    exact: true,
    path: '/auth/advanced-search/:category',
    breadcrumb: 'results',
    component: AdvancedSearch,
    title: 'Properties'
  },
  {
    exact: true,
    path: '/auth/simple-search/:category/:province/:district/:city/:suburb',
    breadcrumb: 'results',
    component: SuburbResults,
    title: 'Properties'
  }
]

const PUBLIC_ROUTES = [
  {
    exact: true,
    title: 'Home',
    path: '/',
    component: Home
  },
  {
    exact: true,
    path: '/search',
    breadcrumb: 'Search',
    component: Search,
    title: 'Search'
  },
  {
    exact: true,
    title: 'Login',
    path: '/login',
    component: Login
  },
  {
    exact: true,
    title: 'Register',
    path: '/register',
    component: Register
  },
  {
    exact: true,
    title: 'Confirm Email',
    path: '/confirm-email',
    component: ConfirmEmail
  },
  {
    exact: true,
    title: 'Forgot Password',
    path: '/forgot-password',
    component: ForgotPassword
  },
  {
    exact: true,
    title: 'Reset Password',
    path: '/reset-password',
    component: ResetPassword
  },
  {
    exact: false,
    path: '/:providerName/callback',
    breadcrumb: 'redirect',
    component: LoginRedirect,
    title: 'redirect'
  },
  // TODO:: May need some improvments
  {
    exact: true,
    path: '/simple-search/:category/:province',
    component: ProvinceResults,
    title: 'Properties'
  },
  {
    exact: true,
    path: '/property/:id',
    breadcrumb: 'property',
    component: PropertyProfile,
    title: 'Property'
  },
  {
    exact: true,
    path: '/simple-search/:category/:province/:district/:city',
    component: CityResults,
    title: 'Properties'
  },
  {
    exact: true,
    path: '/simple-search/:category',
    component: CategoryResults,
    title: 'Properties'
  },
  {
    exact: true,
    path: '/advanced-search/:category',
    component: AdvancedSearch,
    title: 'Properties'
  },
  {
    exact: true,
    path: '/simple-search/:category/:province/:district',
    component: DistrictResults,
    title: 'Properties'
  },
  {
    exact: true,
    path: '/simple-search/:category/:province/:district/:city/:suburb',
    component: SuburbResults,
    title: 'Properties'
  },
  {
    exact: true,
    path: '/about-us',
    component: AboutUs,
    title: 'About Us'
  },
  {
    exact: true,
    path: '/history',
    breadcrumb: 'story',
    component: Story,
    title: 'Story'
  },
  {
    exact: true,
    path: '/how-it-works',
    breadcrumb: 'how it works',
    component: HowItWorks,
    title: 'How It Works'
  },
  {
    exact: true,
    path: '/use-cases',
    breadcrumb: 'use cases',
    component: UseCases,
    title: 'Use Cases'
  },
  {
    exact: true,
    path: '/privacy',
    breadcrumb: 'privacy',
    component: Privacy,
    title: 'Privacy'
  },
  {
    exact: true,
    path: '/terms',
    breadcrumb: 'terms',
    component: Terms,
    title: 'Terms'
  },
  {
    exact: true,
    path: '/team',
    component: Team,
    title: 'Team'
  },
  {
    exact: true,
    path: '/career',
    component: Careers,
    title: 'Career'
  }
]

export { PUBLIC_ROUTES, PRIVATE_ROUTES }
